import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useToast } from '@/components/ui';
import { issueAccount, type AccountIssued } from '@/api/console';
import { fetchAccounts } from '@/api/accounts';
import { applyBulkLevelAssign, OrgAdminApiError } from '@/api/orgAdmin';
import type { KeycloakAccountRow } from '@/pages/Console/Accounts/types';
import type { ScopedAccountsVM } from './useScopedAccounts';

/**
 * A plain trailing-edge debounce over a VALUE. The kit's `useDebouncedSearch` is
 * not this — it debounces a controlled input's `onChange` callback for
 * `TableToolbar`, and returns the immediate local value, so it cannot gate a query
 * key. Two search boxes here (the realm account search and the username
 * duplicate probe) each drive a request per keystroke without it.
 */
function useDebouncedValue(value: string, delay: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/** Frame 35e ↔ 35f: the two modes of the one "Cấp tài khoản" modal. */
export type ProvisionMode = 'create' | 'existing';

/** Frame 35e's "Cách kích hoạt" pair — exactly `POST /accounts`'s `credential_mode`. */
export type CredentialMode = 'invite' | 'temp_password';

const EMPTY_CREATE = {
    fullName: '',
    username: '',
    email: '',
    levelId: '',
    credentialMode: 'invite' as CredentialMode,
};

/** "Nguyễn Thị Vân" → `van.nt`, the shape frame 35e shows as auto-generated. */
export function suggestUsername(fullName: string): string {
    const strip = (value: string) =>
        value
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
    const parts = fullName.trim().split(/\s+/).filter(Boolean).map(strip).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    const given = parts[parts.length - 1];
    const initials = parts.slice(0, -1).map((part) => part[0]).join('');
    return `${given}.${initials}`;
}

/**
 * EAA-1839 (frames 35e + 35f): the "Cấp tài khoản" modal's engine — one hook for
 * both of its modes, because the frames are two states of the same modal (the
 * changelog for this design records them as a single segmented control added on
 * 2026-08-21, not two dialogs).
 *
 * MODE "create" (35e) is two writes, in this order and for this reason:
 * `POST /accounts` mints the Keycloak identity but produces NO `Assignment`, so an
 * account created alone would not appear in this list at all (the same fact
 * `ScopedAccountDialogs` has recorded since EAA-1839). The Level is therefore
 * applied straight after via `bulk-apply`, and a failure between the two is
 * reported as what it is — an account that exists with no Level, i.e. one of the
 * "Chưa có Level" rows this very screen exists to fix — rather than as a failed
 * create.
 *
 * MODE "existing" (35f) is ONE write: `bulk-apply` in the scope org for the chosen
 * sub. The search behind it is realm-wide `GET /accounts?search=`, which is what
 * makes the frame's "Tìm trong toàn bộ EMG Group, không giới hạn nhánh hiện tại"
 * real; that endpoint is `IsSuperAdmin` server-side, so the mode is gated on
 * `console.accounts.view` and the whole tab is absent for an org-admin instead of
 * offering a search that would 403.
 *
 * WHAT IS RENDERED INERT, each for a contract reason:
 *  - "Chuyển đơn vị chính sang đây" (35f's second Kiểu-gắn card).
 *    `POST .../assignments/{id}/transfer` is keyed to an assignment IN ITS CURRENT
 *    ORG, and this modal knows the person, not their grant id — `KeycloakAccountRow.
 *    placement` carries the org name and level name but no assignment id, and
 *    fetching one would mean listing the SOURCE đơn vị's assignments, which the
 *    caller may not administer. The real path is frame 85's per-row "Chuyển đơn vị"
 *    in that đơn vị's own list, which is what the reason says.
 *  - "Hiệu lực Từ–Đến" (35f). `Assignment` has `granted_at` and `revoked_at`, both
 *    server-set; there is no scheduled-start or expiry field to write.
 *  - "Mã nhân sự" and "Cấp bậc" (35e). Neither `POST /accounts` nor `Assignment`
 *    has a field for either.
 *  - "Bắt buộc xác thực 2 lớp" (35e). No 2FA-enforcement write exists.
 *  - The two notify checkboxes. `credential_mode: 'invite'` IS the user-facing
 *    email in mode "create"; there is no notify flag anywhere in mode "existing".
 */
export function useProvisionAccount(vm: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const can = useCan();
    const toast = useToast();

    const canIssue = can('console.accounts.issue');
    const canSearchRealm = can('console.accounts.view');

    const [open, setOpen] = useState(false);
    const [mode, setModeState] = useState<ProvisionMode>('create');
    const [error, setError] = useState<string | null>(null);
    const [issued, setIssued] = useState<AccountIssued | null>(null);

    /* ------------------------------------------------------------ mode: create */

    const [createForm, setCreateForm] = useState(EMPTY_CREATE);
    const [usernameEdited, setUsernameEdited] = useState(false);

    const patchCreate = useCallback((patch: Partial<typeof EMPTY_CREATE>) => {
        setCreateForm((prev) => ({ ...prev, ...patch }));
        setError(null);
    }, []);

    const setFullName = useCallback(
        (fullName: string) => {
            // Frame 35e shows the username as auto-generated from the name, but an
            // operator who types their own must keep it — so the suggestion stops the
            // moment the field is touched by hand.
            setCreateForm((prev) => ({
                ...prev,
                fullName,
                username: usernameEdited ? prev.username : suggestUsername(fullName),
            }));
            setError(null);
        },
        [usernameEdited]
    );

    const setUsername = useCallback((username: string) => {
        setUsernameEdited(true);
        setCreateForm((prev) => ({ ...prev, username }));
        setError(null);
    }, []);

    /**
     * Frame 35e's "kiểm tra trùng" on the username. Real, and realm-wide: a
     * duplicate username is a 409 from `POST /accounts`, and the same
     * `GET /accounts?search=` that powers mode "existing" can answer it first.
     * Only available where that endpoint is — otherwise the check is skipped and the
     * server's own conflict is what the operator sees.
     */
    const usernameProbe = useDebouncedValue(createForm.username, 400);
    const duplicateQuery = useQuery({
        queryKey: ['provision-username-probe', accessToken, usernameProbe],
        queryFn: () =>
            fetchAccounts(config.apiBase, accessToken as string, {
                search: usernameProbe,
                page_size: 5,
            }),
        enabled: Boolean(accessToken) && canSearchRealm && usernameProbe.trim().length >= 3,
        retry: false,
        staleTime: 30_000,
    });
    const usernameTaken = Boolean(
        usernameProbe.trim() &&
            duplicateQuery.data?.results.some(
                (row) => row.username.toLowerCase() === usernameProbe.trim().toLowerCase()
            )
    );

    /* ---------------------------------------------------------- mode: existing */

    const [existingQuery, setExistingQuery] = useState('');
    const [selected, setSelected] = useState<KeycloakAccountRow | null>(null);
    const [existingLevelId, setExistingLevelId] = useState('');
    const debouncedExisting = useDebouncedValue(existingQuery, 350);

    const searchQuery = useQuery({
        queryKey: ['provision-account-search', accessToken, debouncedExisting],
        queryFn: () =>
            fetchAccounts(config.apiBase, accessToken as string, {
                search: debouncedExisting,
                page_size: 10,
            }),
        enabled:
            Boolean(accessToken) && canSearchRealm && debouncedExisting.trim().length >= 2,
        retry: false,
    });

    const searchResults = useMemo(() => searchQuery.data?.results ?? [], [searchQuery.data]);

    /** Frame 35f's two row badges, derived from what the row genuinely carries. */
    const badgeFor = useCallback(
        (row: KeycloakAccountRow): 'in-branch' | 'no-level' | null => {
            if (row.placement?.organization_id === vm.orgId) return 'in-branch';
            if (!row.placement) return 'no-level';
            return null;
        },
        [vm.orgId]
    );

    /* ------------------------------------------------------------------ writes */

    const reset = useCallback(() => {
        setCreateForm(EMPTY_CREATE);
        setUsernameEdited(false);
        setExistingQuery('');
        setSelected(null);
        setExistingLevelId('');
        setError(null);
    }, []);

    const openModal = useCallback(
        (initialMode: ProvisionMode = 'create') => {
            reset();
            setIssued(null);
            setModeState(canIssue ? initialMode : 'existing');
            setOpen(true);
        },
        [reset, canIssue]
    );

    const closeModal = useCallback(() => setOpen(false), []);

    const setMode = useCallback(
        (next: ProvisionMode) => {
            setModeState(next);
            setError(null);
        },
        []
    );

    const createMutation = useMutation({
        mutationFn: async () => {
            const parts = createForm.fullName.trim().split(/\s+/).filter(Boolean);
            const first_name = parts.length > 1 ? parts[parts.length - 1] : parts[0] ?? '';
            const last_name = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
            const account = await issueAccount(config.apiBase, accessToken as string, {
                username: createForm.username.trim(),
                ...(createForm.email.trim() ? { email: createForm.email.trim() } : {}),
                ...(first_name ? { first_name } : {}),
                ...(last_name ? { last_name } : {}),
                credential_mode: createForm.credentialMode,
            });
            if (createForm.levelId) {
                try {
                    await applyBulkLevelAssign(
                        config.apiBase,
                        accessToken as string,
                        vm.orgId as string,
                        { userSubs: [account.sub], levelId: createForm.levelId }
                    );
                } catch {
                    // The identity DOES exist now; saying "failed" would be wrong and
                    // would invite a second create attempt that then 409s.
                    toast.error(
                        t('console.hierarchy.scopedAccounts.provision.levelAfterCreateFailed', {
                            username: account.username,
                        })
                    );
                }
            }
            return account;
        },
        onSuccess: (account) => {
            vm.invalidateList();
            setIssued(account.temp_password ? account : null);
            reset();
            toast.success(
                t('console.hierarchy.scopedAccounts.provision.createdToast', {
                    username: account.username,
                })
            );
            if (!account.temp_password) setOpen(false);
        },
        onError: (err: unknown) => {
            const message =
                err instanceof OrgAdminApiError
                    ? err.message
                    : t('console.hierarchy.scopedAccounts.provision.createFailed');
            setError(message);
            toast.error(message);
        },
    });

    const attachMutation = useMutation({
        mutationFn: () =>
            applyBulkLevelAssign(config.apiBase, accessToken as string, vm.orgId as string, {
                userSubs: [selected?.sub as string],
                levelId: existingLevelId,
            }),
        onSuccess: () => {
            vm.invalidateList();
            const username = selected?.username ?? '';
            reset();
            setOpen(false);
            toast.success(
                t('console.hierarchy.scopedAccounts.provision.attachedToast', { username })
            );
        },
        onError: (err: unknown) => {
            const message =
                err instanceof OrgAdminApiError
                    ? err.message
                    : t('console.hierarchy.scopedAccounts.provision.attachFailed');
            setError(message);
            toast.error(message);
        },
    });

    const submit = useCallback(() => {
        if (mode === 'create') {
            if (!createForm.fullName.trim()) {
                setError(t('console.hierarchy.scopedAccounts.provision.validationNameRequired'));
                return;
            }
            if (!createForm.username.trim()) {
                setError(t('console.hierarchy.scopedAccounts.provision.validationUsernameRequired'));
                return;
            }
            if (usernameTaken) {
                setError(t('console.hierarchy.scopedAccounts.provision.validationUsernameTaken'));
                return;
            }
            createMutation.mutate();
            return;
        }
        if (!selected) {
            setError(t('console.hierarchy.scopedAccounts.provision.validationPickAccount'));
            return;
        }
        if (!existingLevelId) {
            setError(t('console.hierarchy.scopedAccounts.provision.validationLevelRequired'));
            return;
        }
        attachMutation.mutate();
    }, [mode, createForm, usernameTaken, selected, existingLevelId, createMutation, attachMutation, t]);

    return {
        open,
        openModal,
        closeModal,
        mode,
        setMode,
        canIssue,
        canSearchRealm,
        error,
        busy: createMutation.isPending || attachMutation.isPending,
        submit,

        issued,
        dismissIssued: () => setIssued(null),

        createForm,
        patchCreate,
        setFullName,
        setUsername,
        usernameTaken,
        usernameChecking: duplicateQuery.isFetching,

        existingQuery,
        setExistingQuery,
        searchResults,
        searching: searchQuery.isFetching,
        selected,
        setSelected,
        existingLevelId,
        setExistingLevelId,
        badgeFor,

        levels: vm.activeLevels,
        orgName: vm.orgName,
    };
}

export type ProvisionAccountVM = ReturnType<typeof useProvisionAccount>;
